import React from "react";
import { Link } from "react-router-dom";
import { TrophyOutlined, CoffeeOutlined, FileTextOutlined, VideoCameraOutlined, ReadOutlined } from "@ant-design/icons";
import { PageHeader } from "@dowty/ui";
import { runtimeValue } from "@dowty/core";
import { useAuth } from "../context/AuthContext";
import { getSsoApiBaseUrl, getSsoStartUrl, isSsoProducerEnabled } from "../ssoConfig";

type SsoApiApp = "saufdings" | "sportdings" | "lerndings";

const APPS = [
    {
        key: "sportdings",
        accent: "#047857",
        accentDark: "#34d399",
        title: "Sportdings",
        description: "Fitness & sports",
        descriptionDe: "Fitness & Sport",
        icon: <TrophyOutlined />,
        url: "https://sportdings.dowtex.com" as string | null,
        runtimeKey: "SPORTDINGS_URL" as string | null,
        ssoStartPath: "/auth/sso/start" as string | null,
        ssoApi: "sportdings" as SsoApiApp | null,
        route: null as string | null,
        restricted: null as string | null,
    },
    {
        key: "saufdings",
        accent: "#c2410c",
        accentDark: "#ff7a59",
        title: "Saufdings",
        description: "Events & beer pong",
        descriptionDe: "Events & Bierpong",
        icon: <CoffeeOutlined />,
        url: "https://saufdings.dowtex.com" as string | null,
        runtimeKey: "SAUFDINGS_URL" as string | null,
        ssoStartPath: "/sso/launch" as string | null,
        ssoApi: "saufdings" as SsoApiApp | null,
        route: null as string | null,
        restricted: null as string | null,
    },
    {
        key: "doc-dig",
        accent: "#6d28d9",
        accentDark: "#8250f2",
        title: "DocDig",
        description: "Document management",
        descriptionDe: "Dokumente verwalten",
        icon: <FileTextOutlined />,
        url: "https://digdoc.dowtex.com" as string | null,
        runtimeKey: "DIGDOC_URL" as string | null,
        ssoStartPath: "/api/auth/sso/start" as string | null,
        ssoApi: null as SsoApiApp | null,
        route: null as string | null,
        restricted: "doc-dig" as string | null,
    },
    {
        key: "dualcam",
        accent: "#2563eb",
        accentDark: "#60a5fa",
        title: "DualCam",
        description: "Dual-camera recordings",
        descriptionDe: "Aufnahmen mit zwei Kameras",
        icon: <VideoCameraOutlined />,
        url: null as string | null,
        runtimeKey: null as string | null,
        ssoStartPath: null as string | null,
        ssoApi: null as SsoApiApp | null,
        route: "/dualcam" as string | null,
        restricted: null as string | null,
    },
    {
        key: "lerndings",
        accent: "#176b63",
        accentDark: "#60c4b1",
        title: "Lerndings",
        description: "Study materials & cards",
        descriptionDe: "Lernmaterialien & Karteikarten",
        icon: <ReadOutlined />,
        url: "https://lerndings.dowtex.com" as string | null,
        runtimeKey: "LERNDINGS_URL" as string | null,
        ssoStartPath: "/sso/launch" as string | null,
        ssoApi: "lerndings" as SsoApiApp | null,
        route: null as string | null,
        restricted: "lerndings" as string | null,
    },
] as const;

export const getAppUrl = (key: typeof APPS[number]["key"]) => {
    const { runtimeKey, url } = APPS.find(app => app.key === key)!;
    return runtimeValue(runtimeKey! as keyof NonNullable<Window["__RUNTIME_CONFIG__"]>) || url!;
};

const Dashboard: React.FC = () => {
    const { user } = useAuth();
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    return (
        <div className="sf-content-column sf-dashboard">
            <PageHeader title={isGerman ? `Willkommen zurück, ${user?.username}` : `Welcome back, ${user?.username}`} />
            <ul className="sf-honeycomb">
                {APPS.map(app => {
                    if (app.restricted && !user?.roles.includes(app.restricted)) return null;
                    const description = isGerman ? app.descriptionDe : app.description;
                    const body = <>
                        <span className="sf-app-icon" aria-hidden="true">{app.icon}</span>
                        <strong>{app.title}</strong>
                        <span className="sf-app-description">{description}</span>
                    </>;
                    const props = {
                        className: "sf-app-tile",
                        "aria-label": `${app.title} - ${description}`,
                        style: { "--app-accent": app.accent, "--app-accent-dark": app.accentDark } as React.CSSProperties,
                    };
                    if (app.route) return <li key={app.key} data-app={app.key}><Link {...props} to={app.route}
                        onKeyDown={e => { if (e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}>{body}</Link></li>;
                    const appUrl = getAppUrl(app.key);
                    let ssoStartUrl: string | null = null;
                    if (isSsoProducerEnabled() && app.ssoStartPath) {
                        const baseUrl = app.ssoApi ? getSsoApiBaseUrl(app.ssoApi) : appUrl;
                        ssoStartUrl = getSsoStartUrl(app.ssoStartPath, baseUrl);
                    }
                    return <li key={app.key} data-app={app.key}><a {...props} href={ssoStartUrl || appUrl} target="_blank" rel="noopener noreferrer">{body}</a></li>;
                })}
            </ul>
        </div>
    );
};

export default Dashboard;
